/**
 * Registers the app-shell service worker (`public/sw.js`).
 *
 * Scope per CLAUDE.md / SPEC.md §8: offline app shell only, nothing
 * PWA-adjacent beyond that. This module only registers the worker — it is
 * a no-op everywhere registration would be unwanted or unsafe:
 *
 * - Unsupported browsers (`'serviceWorker' in navigator` guard).
 * - Dev mode: a service worker intercepting fetches fights Vite's HMR and
 *   dev server, so registration is skipped under `import.meta.env.DEV`.
 *
 * Registration failures are swallowed with a console warning rather than
 * thrown — losing offline support should never break the app.
 *
 * This function is intentionally not called anywhere in this module or
 * elsewhere in this change; the call site is wired up separately.
 */
export function registerServiceWorker(): void {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker
    .register('./sw.js')
    .then(() => navigator.serviceWorker.ready)
    .then(whenControlled)
    .then(warmCache)
    .catch((error: unknown) => {
      console.warn('Service worker registration failed:', error);
    });
}

/**
 * Wait until the worker actually controls this page.
 *
 * `serviceWorker.ready` resolves once a worker is *active*, which is earlier
 * than it taking control — on a first visit the page is still uncontrolled at
 * that point, and a `fetch` from here would bypass the worker entirely and
 * cache nothing. `clients.claim()` in the worker's activate handler is what
 * fires `controllerchange`.
 */
function whenControlled(): Promise<void> {
  if (navigator.serviceWorker.controller) return Promise.resolve();
  return new Promise((resolve) => {
    navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
    // Never hang the promise chain; the next visit warms the cache anyway.
    setTimeout(resolve, 3000);
  });
}

/**
 * Cache the assets this very page loaded.
 *
 * The worker only starts intercepting once it is active, which is *after* the
 * browser has already fetched the main JS and CSS. Without this, a visitor who
 * loads the app once and then goes offline gets the cached shell and no
 * application — offline would only work from the second visit, and SPEC.md §8
 * asks for it to work, full stop.
 *
 * Re-requesting through `fetch` lets the worker's own cache-first handler store
 * them, so the cache name and strategy stay owned by `sw.js`. The entries are
 * already in the HTTP cache, so this costs no real network traffic.
 */
function warmCache(): void {
  const sameOrigin = performance
    .getEntriesByType('resource')
    .map((entry) => entry.name)
    .filter((url) => url.startsWith(self.location.origin))
    .filter((url) => /\.(js|css|svg|webmanifest)$/.test(new URL(url).pathname));

  for (const url of new Set(sameOrigin)) {
    void fetch(url, { cache: 'force-cache' }).catch(() => undefined);
  }
}
